import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getBasicInfo,
  saveBasicInfo,
  getDependents,
  saveDependents,
  getDeductionFiles,
  uploadDeductionFiles,
  deleteDeductionFile,
} from '../services/userService';
import ConfirmModal from '../components/ConfirmModal';
import './BasicInfoPage.css';

const BasicInfoPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [basicInfo, setBasicInfo] = useState({
    name: '',
    company: '',
    taxYear: '',
    email: '',
    residentNumber: '',
    address: '',
  });
  const [dependents, setDependents] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDependents, setSelectedDependents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveMessageType, setSaveMessageType] = useState('');
  const saveTimeoutRef = useRef(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState('');
  const [confirmPayload, setConfirmPayload] = useState(null);
  const isAddressValid = basicInfo.address.trim().length > 0;

  const isSubmitted = user?.status === 'submitted';
  const isEmptyDependent = (dep) =>
    !dep.name && !dep.familyRelation && !dep.residentNumber;

  useEffect(() => {
    loadData();
  }, []);

  // 부양가족 정보 자동 저장 (debounce)
  useEffect(() => {
    // 초기 로드 시에는 저장하지 않음
    if (loading) return;
    
    // 본인 정보만 있는 경우 저장하지 않음
    const hasNonSelfDependents = dependents.some(
      dep => dep.familyRelation && dep.familyRelation !== 'self' && !isEmptyDependent(dep)
    );
    if (!hasNonSelfDependents) return;

    // debounce: 2초 후 자동 저장
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await saveDependents(
          dependents
            .filter(dep => dep.familyRelation !== 'self' && !isEmptyDependent(dep))
            .map(dep => ({
              name: dep.name,
              familyRelation: dep.familyRelation,
              relationship: dep.relationship || '',
              residentNumber: dep.residentNumber,
              incomeBelow1m: dep.incomeBelow1m,
              isDisabled: dep.isDisabled,
            }))
        );
        console.log('부양가족 정보가 자동 저장되었습니다.');
      } catch (error) {
        console.error('자동 저장 실패:', error);
      }
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [dependents, loading]);

  const showSaveMessage = (message, type) => {
    setSaveMessage(message);
    setSaveMessageType(type);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => setSaveMessage(''), 3000);
  };

  const openConfirm = (action, message, payload = null) => {
    setConfirmAction(action);
    setConfirmMessage(message);
    setConfirmPayload(payload);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmMessage('');
    setConfirmAction('');
    setConfirmPayload(null);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [basicData, dependentsData, filesData] = await Promise.all([
        getBasicInfo().catch(() => ({})),
        getDependents().catch(() => ({ dependents: [] })),
        getDeductionFiles().catch(() => ({ files: [] })),
      ]);

      if (basicData) {
        setBasicInfo({
          name: basicData.name || '',
          company: basicData.company || '',
          taxYear: basicData.tax_year || '',
          email: basicData.email || '',
          residentNumber: basicData.resident_number || '',
          address: basicData.address || '',
        });
      }

      let dependentsList = dependentsData.dependents || [];
      
      // 본인 정보가 없으면 자동 추가
      const hasSelf = dependentsList.some(dep => dep.familyRelation === 'self' || dep.family_relation === 'self');
      if (!hasSelf && basicData) {
        const selfDependent = {
          id: 'self',
          name: basicData.name || '',
          familyRelation: 'self',
          relationship: '본인',
          residentNumber: basicData.resident_number || '',
          incomeBelow1m: null,
          isDisabled: null,
        };
        dependentsList = [selfDependent, ...dependentsList];
      } else {
        // 본인 정보를 첫 번째로 정렬
        const selfIndex = dependentsList.findIndex(dep => dep.familyRelation === 'self' || dep.family_relation === 'self');
        if (selfIndex > 0) {
          const self = dependentsList[selfIndex];
          dependentsList.splice(selfIndex, 1);
          dependentsList.unshift(self);
        }
      }

      // 필드명 통일 (snake_case -> camelCase)
      dependentsList = dependentsList.map(dep => ({
        ...dep,
        familyRelation: dep.familyRelation || dep.family_relation || '',
        residentNumber: dep.residentNumber || dep.resident_number || '',
        incomeBelow1m: dep.incomeBelow1m !== undefined ? dep.incomeBelow1m : (dep.income_below_1m !== undefined ? dep.income_below_1m : false),
        isDisabled: dep.isDisabled !== undefined ? dep.isDisabled : (dep.is_disabled !== undefined ? dep.is_disabled : false),
      }));

      setDependents(dependentsList);
      setUploadedFiles(filesData.files || []);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBasicInfoChange = (field, value) => {
    setBasicInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddDependent = () => {
    if (isSubmitted) return;
    setDependents([
      ...dependents,
      {
        id: Date.now(),
        name: '',
        familyRelation: '',
        relationship: '',
        residentNumber: '',
        incomeBelow1m: false,
        isDisabled: false,
      },
    ]);
  };

  const handleDependentChange = (index, field, value) => {
    if (isSubmitted) return;
    const updated = [...dependents];
    const dependent = updated[index];
    const isSelf = dependent.familyRelation === 'self';

    if (isSelf && (field === 'familyRelation' || field === 'name' || field === 'residentNumber')) {
      showSaveMessage('본인 정보는 수정할 수 없습니다.', 'error');
      return;
    }

    if (field === 'familyRelation' && value === 'self' && !isSelf) {
      showSaveMessage('본인은 이미 포함되어 있습니다.', 'error');
      return;
    }

    if (field === 'familyRelation') {
      const relationshipMap = {
        'self': '본인',
        'spouse': '배우자',
        'father': '아버지',
        'mother': '어머니',
        'grandfather': '할아버지',
        'grandmother': '할머니',
        'spouse_father': '배우자의 아버지',
        'spouse_mother': '배우자의 어머니',
        'spouse_grandfather': '배우자의 할아버지',
        'spouse_grandmother': '배우자의 할머니',
        'child': '자녀',
        'sibling': '형제자매',
      };
      updated[index] = { ...dependent, [field]: value, relationship: relationshipMap[value] || '' };
    } else {
      updated[index] = { ...dependent, [field]: value };
    }
    setDependents(updated);
  };

  const handleSelectDependent = (index) => {
    const dependent = dependents[index];
    if (dependent.familyRelation === 'self') return;
    
    if (selectedDependents.includes(index)) {
      setSelectedDependents(selectedDependents.filter(i => i !== index));
    } else {
      setSelectedDependents([...selectedDependents, index]);
    }
  };

  const handleRemoveSelectedDependents = () => {
    if (isSubmitted) return;
    if (selectedDependents.length === 0) {
      showSaveMessage('삭제할 행을 선택해주세요.', 'error');
      return;
    }
    
    const toRemove = selectedDependents.filter(index => {
      const dependent = dependents[index];
      return dependent.familyRelation !== 'self';
    });

    if (toRemove.length === 0) {
      showSaveMessage('본인 정보는 삭제할 수 없습니다.', 'error');
      return;
    }

    openConfirm('removeDependents', `선택한 ${toRemove.length}개의 행을 삭제하시겠습니까?`, {
      removeIndexes: toRemove,
    });
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter((file) => {
      const maxSize = 100 * 1024 * 1024; // 100MB
      const allowedExtensions = [
        'pdf',
        'jpg',
        'jpeg',
        'png',
        'hwp',
        'xlsx',
        'xls',
        'doc',
        'docx',
        'zip',
      ];
      const extension = file.name.split('.').pop().toLowerCase();

      if (file.size > maxSize) {
        showSaveMessage(`${file.name} 파일이 100MB를 초과합니다.`, 'error');
        return false;
      }

      if (!allowedExtensions.includes(extension)) {
        showSaveMessage(`${file.name} 파일의 확장자가 허용되지 않습니다.`, 'error');
        return false;
      }

      return true;
    });

    if (uploadedFiles.length + selectedFiles.length + validFiles.length > 100) {
      showSaveMessage('최대 100개의 파일만 업로드할 수 있습니다.', 'error');
      return;
    }

    setSelectedFiles([...selectedFiles, ...validFiles]);
    e.target.value = '';
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      showSaveMessage('업로드할 파일을 선택해주세요.', 'error');
      return;
    }

    setUploading(true);
    try {
      await uploadDeductionFiles(selectedFiles);
      showSaveMessage('파일 업로드가 완료되었습니다.', 'success');
      setSelectedFiles([]);
      // 파일 목록만 다시 불러오기 (부양가족 정보는 유지)
      const filesData = await getDeductionFiles().catch(() => ({ files: [] }));
      setUploadedFiles(filesData.files || []);
    } catch (error) {
      showSaveMessage('파일 업로드에 실패했습니다.', 'error');
      console.error('파일 업로드 실패:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId, fileName) => {
    if (isSubmitted) {
      showSaveMessage('제출 완료 후에는 파일을 삭제할 수 없습니다.', 'error');
      return;
    }

    openConfirm('deleteFile', `"${fileName}" 파일을 삭제하시겠습니까?`, { fileId });
  };

  const performDeleteFile = async (fileId) => {
    setDeletingFile(fileId);
    try {
      await deleteDeductionFile(fileId);
      showSaveMessage('파일이 삭제되었습니다.', 'success');
      // 파일 목록만 다시 불러오기 (부양가족 정보는 유지)
      const filesData = await getDeductionFiles().catch(() => ({ files: [] }));
      setUploadedFiles(filesData.files || []);
    } catch (error) {
      showSaveMessage('파일 삭제에 실패했습니다.', 'error');
      console.error('파일 삭제 실패:', error);
    } finally {
      setDeletingFile(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveBasicInfo({ address: basicInfo.address }),
        saveDependents(dependents.filter(dep => dep.familyRelation !== 'self' && !isEmptyDependent(dep)).map(dep => ({
          name: dep.name,
          familyRelation: dep.familyRelation,
          relationship: dep.relationship || '',
          residentNumber: dep.residentNumber,
          incomeBelow1m: dep.incomeBelow1m,
          isDisabled: dep.isDisabled,
        }))),
      ]);
      showSaveMessage('기본정보가 저장되었습니다.', 'success');
    } catch (error) {
      showSaveMessage('저장에 실패했습니다.', 'error');
      console.error('저장 실패:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    const payload = confirmPayload;
    closeConfirm();
    if (action === 'removeDependents' && payload?.removeIndexes) {
      const remaining = dependents.filter((_, index) => !payload.removeIndexes.includes(index));
      setDependents(remaining);
      setSelectedDependents([]);
    } else if (action === 'deleteFile' && payload?.fileId) {
      await performDeleteFile(payload.fileId);
    }
  };

  const handleNext = async () => {
    if (!isAddressValid) {
      showSaveMessage('주소지를 작성하셔야 다음 페이지로 전환이 가능합니다.', 'error');
      return;
    }
    await handleSave();
    navigate('/survey');
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  return (
    <div className="basic-info-page">
      <h1>기본정보 작성</h1>

      <section className="section">
        <h2>기본 정보</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>성함 *</label>
            <input
              type="text"
              value={basicInfo.name}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>회사명 *</label>
            <input
              type="text"
              value={basicInfo.company}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>과세연도 *</label>
            <input
              type="number"
              value={basicInfo.taxYear}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>이메일 *</label>
            <input
              type="email"
              value={basicInfo.email}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>주민등록번호 *</label>
            <input
              type="text"
              value={basicInfo.residentNumber}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group full-width">
            <label>주소지 *</label>
            <input
              type="text"
              value={basicInfo.address}
              onChange={(e) => handleBasicInfoChange('address', e.target.value)}
              readOnly={isSubmitted}
              placeholder="주소를 입력하세요"
            />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-header">
          <h2>부양가족 정보</h2>
          <div className="dependent-actions">
            <button onClick={handleAddDependent} className="add-row-btn" disabled={isSubmitted}>
              + 행 추가
            </button>
            <button 
              onClick={handleRemoveSelectedDependents} 
              className="remove-row-btn" 
              disabled={selectedDependents.length === 0 || isSubmitted}
            >
              선택 행 삭제
            </button>
          </div>
        </div>
        {dependents.length > 0 ? (
          <div className="dependents-table-wrapper">
            <table className="dependents-table">
              <thead>
                <tr>
                  <th className="checkbox-col"></th>
                  <th>성함</th>
                  <th>가족관계</th>
                  <th>관계</th>
                  <th>주민등록번호</th>
                  <th>소득금액 100만원 이하</th>
                  <th>장애인</th>
                </tr>
              </thead>
              <tbody>
                {dependents.map((dependent, index) => {
                  const isSelf = dependent.familyRelation === 'self';
                  return (
                    <tr
                      key={dependent.id || index}
                      className={isSelf ? 'self-row' : ''}
                    >
                      <td className="checkbox-col">
                        {!isSelf && (
                          <input
                            type="checkbox"
                            checked={selectedDependents.includes(index)}
                            onChange={() => handleSelectDependent(index)}
                            disabled={isSubmitted}
                          />
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={dependent.name}
                          onChange={(e) =>
                            handleDependentChange(index, 'name', e.target.value)
                          }
                          className={`table-input ${isSelf ? 'readonly' : ''}`}
                          readOnly={isSelf || isSubmitted}
                        />
                      </td>
                      <td>
                        <select
                          value={dependent.familyRelation || ''}
                          onChange={(e) =>
                            handleDependentChange(index, 'familyRelation', e.target.value)
                          }
                          className={`table-select ${isSelf ? 'readonly' : ''}`}
                          disabled={isSelf || isSubmitted}
                        >
                          <option value="">선택하세요</option>
                          <option value="self">본인</option>
                          <option value="spouse">배우자</option>
                          <option value="father">아버지</option>
                          <option value="mother">어머니</option>
                          <option value="grandfather">할아버지</option>
                          <option value="grandmother">할머니</option>
                          <option value="spouse_father">배우자의 아버지</option>
                          <option value="spouse_mother">배우자의 어머니</option>
                          <option value="spouse_grandfather">배우자의 할아버지</option>
                          <option value="spouse_grandmother">배우자의 할머니</option>
                          <option value="child">자녀</option>
                          <option value="sibling">형제자매</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={dependent.relationship}
                          readOnly
                          className="table-input readonly"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={dependent.residentNumber}
                          onChange={(e) =>
                            handleDependentChange(
                              index,
                              'residentNumber',
                              e.target.value
                            )
                          }
                          className={`table-input ${isSelf ? 'readonly' : ''}`}
                          readOnly={isSelf || isSubmitted}
                        />
                      </td>
                      <td>
                        {isSelf ? (
                          <span className="readonly-cell">-</span>
                        ) : (
                          <select
                            value={dependent.incomeBelow1m ? 'yes' : 'no'}
                            onChange={(e) =>
                              handleDependentChange(index, 'incomeBelow1m', e.target.value === 'yes')
                            }
                            className={`table-select ${isSubmitted ? 'readonly' : ''}`}
                            disabled={isSubmitted}
                          >
                            <option value="no">아니오</option>
                            <option value="yes">예</option>
                          </select>
                        )}
                      </td>
                      <td>
                        {isSelf ? (
                          <span className="readonly-cell">-</span>
                        ) : (
                          <select
                            value={dependent.isDisabled ? 'yes' : 'no'}
                            onChange={(e) =>
                              handleDependentChange(index, 'isDisabled', e.target.value === 'yes')
                            }
                            className={`table-select ${isSubmitted ? 'readonly' : ''}`}
                            disabled={isSubmitted}
                          >
                            <option value="no">아니오</option>
                            <option value="yes">예</option>
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>부양가족 정보가 없습니다.</p>
        )}
      </section>

      <section className="section">
        <h2>공제자료 파일 업로드</h2>
        <div className="file-upload-section">
          <div className="file-input-wrapper">
            <input
              type="file"
              id="file-input"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png,.hwp,.xlsx,.xls,.doc,.docx,.zip"
              disabled={isSubmitted || uploading}
            />
            <label htmlFor="file-input" className="file-input-label">
              파일 선택
            </label>
            <span className="file-info">
              최대 100MB, 최대 100개 (pdf, jpg, jpeg, png, hwp, xlsx, xls, doc, docx, zip)
            </span>
          </div>

          {selectedFiles.length > 0 && (
            <div className="files-to-upload">
              <h4>업로드할 파일 ({selectedFiles.length}개)</h4>
              <div className="files-list">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item">
                    <span>{file.name}</span>
                    <span className="file-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <button
                      onClick={() => handleRemoveSelectedFile(index)}
                      className="file-remove-btn"
                      disabled={uploading}
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleFileUpload}
                className="upload-btn"
                disabled={uploading}
              >
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            </div>
          )}

          {uploadedFiles.length > 0 && (
            <div className="uploaded-files">
              <h4>업로드된 파일 목록 ({uploadedFiles.length}개)</h4>
              <div className="files-list">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="file-item uploaded">
                    <span className="file-name">{file.original_name || file.name}</span>
                    <span className="file-size">
                      {file.file_size ? (file.file_size / 1024 / 1024).toFixed(2) : '-'} MB
                    </span>
                    <span className="file-date">
                      {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('ko-KR') : '-'}
                    </span>
                    {!isSubmitted && (
                      <button
                        onClick={() => handleDeleteFile(file.id, file.original_name || file.name)}
                        className="file-delete-btn"
                        disabled={deletingFile === file.id}
                      >
                        {deletingFile === file.id ? '삭제 중...' : '삭제'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="action-buttons">
        {saveMessage && (
          <div className={`save-message ${saveMessageType}`}>
            {saveMessage}
          </div>
        )}
        <button onClick={handleSave} className="save-btn" disabled={saving || isSubmitted}>
          {saving ? '저장 중...' : '저장'}
        </button>
        <button
          onClick={handleNext}
          className="next-btn"
          disabled={saving || isSubmitted || !isAddressValid}
          title={!isAddressValid ? '주소지를 작성하셔야 다음 페이지로 전환이 가능합니다.' : ''}
        >
          다음으로
        </button>
      </div>
      <ConfirmModal
        open={confirmOpen}
        title="확인"
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default BasicInfoPage;
